import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBellOff,
  FiGlobe,
  FiHeadphones,
  FiLock,
  FiMessageCircle,
  FiMic,
  FiShield,
  FiUserPlus,
  FiVideo,
} from 'react-icons/fi';

type PublicPageSlug =
  | 'features'
  | 'privacy'
  | 'how-it-works'
  | 'silent-messaging'
  | 'browser-video-calling'
  | 'about'
  | 'blog';

interface PublicPageProps {
  slug: PublicPageSlug;
}

const navItems = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/silent-messaging', label: 'Silent messaging' },
  { to: '/browser-video-calling', label: 'Video calling' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/about', label: 'About' },
  { to: '/blog', label: 'Blog' },
];

const pageData = {
  features: {
    title: 'Whizpr Features',
    eyebrow: 'Text, voice, audio and video',
    description:
      'Whizpr brings private conversations, voice messages, audio calls, and video calls into one quiet browser-based messaging space.',
    icon: FiMessageCircle,
    sections: [
      {
        title: 'Messaging without notification pressure',
        body:
          'Whizpr is designed for conversations that do not demand attention every minute. You can open the app when you want to talk and step away without constant interruption.',
      },
      {
        title: 'Voice messages for when typing is too slow',
        body:
          'Send short voice notes when tone matters, when your hands are busy, or when a message needs to feel more personal than plain text.',
      },
      {
        title: 'Audio and video calls in the browser',
        body:
          'Start real-time calls from the web app, so people can connect from a browser without installing a separate desktop or mobile app first.',
      },
    ],
    highlights: [
      { icon: FiBellOff, label: 'No notification pressure' },
      { icon: FiMic, label: 'Voice messages' },
      { icon: FiHeadphones, label: 'Audio calls' },
      { icon: FiVideo, label: 'Video calls' },
    ],
  },
  privacy: {
    title: 'Privacy at Whizpr',
    eyebrow: 'Privacy-focused messaging',
    description:
      'Whizpr is built for private conversations without public profiles, public feeds, or advertising based on your chats.',
    icon: FiShield,
    sections: [
      {
        title: 'Private by product design',
        body:
          'Whizpr keeps the experience centered on the people you choose. There are no public timelines, follower counts, or discovery feeds pushing your conversations into public space.',
      },
      {
        title: 'Clear language over vague promises',
        body:
          'We describe Whizpr as privacy-focused because the app processes account, message, media, invite, and service data needed to run the product. For full legal details, read the Privacy Policy.',
      },
      {
        title: 'No chat-based advertising',
        body:
          'Your private conversations are not used for advertising. The goal is simple communication, not turning your messages into targeting signals.',
      },
    ],
    highlights: [
      { icon: FiLock, label: 'Private conversations' },
      { icon: FiShield, label: 'No chat-based ads' },
      { icon: FiUserPlus, label: 'Invite people you choose' },
      { icon: FiGlobe, label: 'Browser access' },
    ],
  },
  'how-it-works': {
    title: 'How Whizpr Works',
    eyebrow: 'Start in minutes',
    description:
      'Create your account, invite someone you trust, and begin a quieter text, voice, audio, or video conversation from your browser.',
    icon: FiUserPlus,
    sections: [
      {
        title: 'Create your account',
        body:
          'Sign in to Whizpr and set up the basic profile your friends will recognize inside their conversation list.',
      },
      {
        title: 'Invite someone',
        body:
          'Use Whizpr invitations to connect with the people you choose instead of broadcasting yourself through a public social graph.',
      },
      {
        title: 'Start the conversation',
        body:
          'Use text, voice messages, audio calling, or video calling depending on what the moment needs.',
      },
    ],
    highlights: [
      { icon: FiLock, label: 'Create account' },
      { icon: FiUserPlus, label: 'Invite a friend' },
      { icon: FiMessageCircle, label: 'Start chatting' },
      { icon: FiVideo, label: 'Call in browser' },
    ],
  },
  'silent-messaging': {
    title: 'Silent Messaging',
    eyebrow: 'Conversations without the noise',
    description:
          'Silent messaging is Whizpr\'s simple idea: communication should be available without becoming another source of pressure.',
    icon: FiBellOff,
    sections: [
      {
        title: 'Less urgency by default',
        body:
          'Most messaging apps compete for attention. Whizpr is shaped around calmer use, so people can talk when they are ready.',
      },
      {
        title: 'No public performance layer',
        body:
          'Whizpr focuses on direct conversations instead of public profiles, status chasing, or social metrics.',
      },
      {
        title: 'Built for intentional contact',
        body:
          'The app works best for people who want a quieter place for meaningful personal conversations, small groups, or focused calls.',
      },
    ],
    highlights: [
      { icon: FiBellOff, label: 'No noise' },
      { icon: FiMessageCircle, label: 'Direct chats' },
      { icon: FiLock, label: 'Private space' },
      { icon: FiShield, label: 'Calmer design' },
    ],
  },
  'browser-video-calling': {
    title: 'Browser Video Calling',
    eyebrow: 'Video calls without installing an app first',
    description:
      'Whizpr supports browser-based video calling so conversations can move from messages to face-to-face calls with less friction.',
    icon: FiVideo,
    sections: [
      {
        title: 'Call from the same place you chat',
        body:
          'Keep messages, voice notes, audio calls, and video calls together instead of switching between different apps for every type of conversation.',
      },
      {
        title: 'Useful for quick personal calls',
        body:
          'Browser video calling is a practical fit for friends, family, remote check-ins, and simple private conversations.',
      },
      {
        title: 'Designed for low-friction access',
        body:
          'Because Whizpr runs in the browser, people can start from a link or web session without first committing to a full app install.',
      },
    ],
    highlights: [
      { icon: FiVideo, label: 'Video calls' },
      { icon: FiHeadphones, label: 'Audio calls' },
      { icon: FiGlobe, label: 'Browser-based' },
      { icon: FiMessageCircle, label: 'Chat included' },
    ],
  },
  about: {
    title: 'About Whizpr',
    eyebrow: 'A quieter place to talk',
    description:
      'Whizpr was created for people who want private conversations without turning every message into another demand for attention.',
    icon: FiMessageCircle,
    sections: [
      {
        title: 'Why Whizpr exists',
        body:
          'Messaging has become crowded, public, and noisy. Whizpr starts from a smaller promise: make it easy to talk to chosen people in a focused private space.',
      },
      {
        title: 'What we are building toward',
        body:
          'The goal is a dependable web messaging app with text, voice messages, audio calling, video calling, and privacy-focused defaults.',
      },
      {
        title: 'What Whizpr is not',
        body:
          'Whizpr is not trying to be a social network, a public profile platform, or a feed that asks people to keep performing.',
      },
    ],
    highlights: [
      { icon: FiBellOff, label: 'Quiet by design' },
      { icon: FiLock, label: 'Private by focus' },
      { icon: FiGlobe, label: 'Web-first' },
      { icon: FiVideo, label: 'Calls included' },
    ],
  },
  blog: {
    title: 'Whizpr Blog',
    eyebrow: 'Ideas for calmer communication',
    description:
      'Articles about silent messaging, browser-based calling, private conversations, and building healthier online communication habits.',
    icon: FiMessageCircle,
    sections: [
      {
        title: 'What is silent messaging?',
        body:
          'Silent messaging is a product approach that reduces notification pressure and makes conversations feel more intentional.',
      },
      {
        title: 'Why browser-based video calling matters',
        body:
          'Browser calling lowers the barrier to a quick conversation because people can connect without installing another app first.',
      },
      {
        title: 'How to choose a private messaging app',
        body:
          'Look for clear privacy language, understandable account controls, limited public exposure, and a product model that does not rely on reading your conversations for ads.',
      },
    ],
    highlights: [
      { icon: FiBellOff, label: 'Silent messaging' },
      { icon: FiVideo, label: 'Browser calls' },
      { icon: FiShield, label: 'Privacy notes' },
      { icon: FiMessageCircle, label: 'Product updates' },
    ],
  },
};

const PublicPage: React.FC<PublicPageProps> = ({ slug }) => {
  const page = pageData[slug];
  const Icon = page.icon;

  useEffect(() => {
    document.title = `${page.title} | Whizpr`;
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute('content', page.description);
  }, [page.description, page.title]);

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#02030B] text-white">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_12%,rgba(126,63,242,0.42),transparent_30%),radial-gradient(circle_at_8%_38%,rgba(117,55,213,0.32),transparent_34%),radial-gradient(circle_at_86%_54%,rgba(58,125,255,0.24),transparent_26%),linear-gradient(180deg,#02030B_0%,#0A0820_48%,#02030B_100%)]" />
      <div className="pointer-events-none absolute left-[-110px] top-[260px] h-72 w-72 rounded-full border border-[#7C3AED]/30" />
      <div className="pointer-events-none absolute right-[-92px] top-[420px] h-56 w-56 rounded-full border border-[#3B82F6]/30" />

      <div className="relative border-b border-white/10 bg-[#02030B]/80 px-5 py-4 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/" className="inline-flex items-center gap-3">
            <img src="/whizpr-logo.png" alt="Whizpr logo" className="h-10 w-10 rounded-xl object-cover shadow-[0_0_28px_rgba(124,58,237,0.72)]" />
            <span className="font-brand text-xl font-extrabold tracking-normal">whizpr</span>
          </Link>
          <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold text-[#D9D7FF]">
            {navItems.map((item) => (
              <Link key={item.to} to={item.to} className="transition hover:text-[#8B5CF6]">
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      <section className="relative px-5 py-8 sm:py-14">
        <div className="mx-auto w-full max-w-5xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#B9B5D7] transition hover:text-white"
          >
            <FiArrowLeft className="h-4 w-4" />
            Back to home
          </Link>

          <header className="mt-8 grid gap-8 border-b border-white/10 pb-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/70 bg-[#07050F]/70 px-4 py-2 text-xs font-extrabold uppercase tracking-normal text-white shadow-[0_0_30px_rgba(124,58,237,0.35)]">
                <Icon className="h-4 w-4" />
                {page.eyebrow}
              </div>
              <h1 className="mt-5 max-w-3xl text-4xl font-black leading-tight tracking-normal text-white sm:text-5xl">
                {page.title}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-[#D9D7FF] sm:text-lg sm:leading-8">
                {page.description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {page.highlights.map(({ icon: HighlightIcon, label }) => (
                <div key={label} className="rounded-lg border border-[#7C3AED]/35 bg-[#0A0820]/75 p-4 shadow-[0_14px_36px_rgba(0,0,0,0.24)]">
                  <HighlightIcon className="h-6 w-6 text-[#22D3EE]" />
                  <p className="mt-3 text-sm font-extrabold leading-5 text-white">{label}</p>
                </div>
              ))}
            </div>
          </header>

          <div className="grid gap-8 py-10 lg:grid-cols-[0.72fr_0.28fr]">
            <div className="space-y-8">
              {page.sections.map((section) => (
                <section key={section.title}>
                  <h2 className="text-2xl font-extrabold tracking-normal text-white">{section.title}</h2>
                  <p className="mt-3 text-base leading-8 text-[#D9D7FF]">{section.body}</p>
                </section>
              ))}
            </div>

            <aside className="h-fit rounded-lg border border-[#7C3AED]/35 bg-[#07050F]/75 p-5 shadow-[0_22px_56px_rgba(30,10,80,0.35)] backdrop-blur">
              <h2 className="text-lg font-extrabold tracking-normal text-white">Try Whizpr</h2>
              <p className="mt-3 text-sm leading-6 text-[#D9D7FF]">
                Open the web app, invite someone you trust, and start a quieter private conversation.
              </p>
              <Link
                to="/login"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] px-4 py-3 text-sm font-extrabold text-white shadow-[0_18px_40px_rgba(59,130,246,0.28)] transition hover:brightness-110"
              >
                <FiMessageCircle className="h-5 w-5" />
                Open Whizpr
              </Link>
              <Link
                to="/privacy-policy"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#8B5CF6]/45 px-4 py-3 text-sm font-extrabold text-white transition hover:bg-[#7C3AED]/15"
              >
                <FiShield className="h-5 w-5" />
                Privacy Policy
              </Link>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PublicPage;
